import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle, Package, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi, fmtDate } from '../../lib/api';
import PullToRefresh from '../../components/PullToRefresh';

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('pending');
  const navigate = useNavigate();

  const load = () =>
    adminApi.get(`/admin/orders${filter ? `?status=${filter}` : ''}`).then((r) => setOrders(r.data));
  useEffect(() => { load(); }, [filter]);

  const confirm = async (id) => {
    try {
      const r = await adminApi.post(`/admin/orders/${id}/confirm`);
      toast.success(`Draft invoice ${r.data.invoice_number} created. Review & send.`);
      navigate(`/admin/orders/${r.data.invoice_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };
  const reject = async (id) => {
    const reason = window.prompt('Reason for declining this quote request?');
    if (!reason) return;
    try {
      await adminApi.post(`/admin/orders/${id}/reject`, { reason });
      toast.success('Quote declined');
      load();
    } catch {
      toast.error('Failed');
    }
  };

  return (
    <PullToRefresh onRefresh={load}>
    <div className="fade-in space-y-6" data-testid="requests-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-semibold">Requests</h1>
          <p className="text-sm text-[#6b7280] mt-1">
            Customer quote requests. Review, check availability, then convert to a draft order where you'll set pricing.
          </p>
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="text-sm border border-[#e7e7e7] rounded-lg px-3 py-2"
          data-testid="orders-filter"
        >
          <option value="pending">Pending</option>
          <option value="confirmed">Invoiced</option>
          <option value="rejected">Declined</option>
          <option value="">All</option>
        </select>
      </div>

      {orders.length === 0 ? (
        <div className="card p-12 text-center text-sm text-[#6b7280]">
          <Package size={32} className="mx-auto mb-2 text-[#d4d4d4]" />
          No quote requests {filter && `(${filter})`}
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => {
            const totalUnits = (o.items || []).reduce((s, i) => s + (i.qty || 0), 0);
            return (
              <div key={o.order_id} className="card p-5" data-testid={`order-${o.order_id}`}>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="mono font-semibold">{o.order_number}</div>
                      <span className="text-xs text-[#6b7280]">{fmtDate(o.created_at)}</span>
                    </div>
                    <div className="text-sm font-medium mt-1">{o.customer_name}</div>
                    <div className="text-xs text-[#6b7280]">{o.customer_email}</div>
                    {o.notes && <div className="text-xs italic text-[#6b7280] mt-2">"{o.notes}"</div>}
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-semibold mono">{totalUnits} units</div>
                    <div className="text-xs text-[#6b7280] mt-0.5">{(o.items || []).length} line(s)</div>
                    {o.status === 'pending' ? (
                      <div className="flex gap-2 mt-3 flex-wrap justify-end">
                        <button
                          onClick={() => reject(o.order_id)}
                          className="btn-ghost text-sm flex items-center gap-1"
                          data-testid={`reject-${o.order_id}`}
                        >
                          <XCircle size={14} /> Decline
                        </button>
                        <button
                          onClick={() => confirm(o.order_id)}
                          className="btn-brand text-sm flex items-center gap-1"
                          data-testid={`confirm-${o.order_id}`}
                        >
                          <CheckCircle2 size={14} /> Create Draft Invoice
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="text-xs font-semibold mt-2 text-[#6b7280] uppercase tracking-wide">
                          {o.status === 'confirmed' ? 'Invoiced' : o.status}
                        </div>
                        {o.invoice_id && (
                          <button
                            onClick={() => navigate(`/admin/orders/${o.invoice_id}`)}
                            className="text-xs text-[#d92027] font-semibold mt-1 flex items-center gap-1 ml-auto"
                          >
                            View invoice <ArrowRight size={12} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-[#f3f3f3] space-y-1 text-sm">
                  {(o.items || []).map((it, i) => (
                    <div key={i} className="flex justify-between">
                      <span>{it.name}</span>
                      <span className="mono text-[#6b7280]">× {it.qty}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
    </PullToRefresh>
  );
}
